// Export CSV of the board: the same columns and cell formats as the iPhone's export (CSVService.exportRecords,
// GroundTeam/Services/CSVService.swift), so a file from the console and one from a phone line up in a spreadsheet.
// Pure; the page turns the text into a download.
//
// Two additions over iOS, both for spreadsheets: a cell a spreadsheet would run as a formula ("=…", "@…", "-cmd|…")
// gets a leading apostrophe (a rep's notes must never execute on a manager's machine), and a carriage return also
// forces quoting.

import { RECORD_TYPE_TITLES } from '@/contracts/enums';
import type { PipelinePayload, RecordPayload, TerritoryPayload, UserPayload, VisitResultPayload } from '@/contracts/models';
import { parseWireDate, toWireDate } from '@/contracts/wireDate';
import { statsOf, type DerivedStats } from '@/domain/derived';
import { stageFor } from '@/domain/pipelines';
import { lookup, userName } from '@/domain/util';

/** CSVService.exportRecords' header, in its order. */
export const CSV_HEADER = [
  'Type',
  'Name',
  'Company',
  'Title',
  'Street',
  'City',
  'State',
  'Postal Code',
  'Latitude',
  'Longitude',
  'Phone',
  'Email',
  'Website',
  'Stage',
  'Priority',
  'Value',
  'Owner',
  'Territory',
  'Last Visit',
  'Last Visit Result',
  'Visits',
  'Tags',
  'Notes',
  'Created',
] as const;

export interface CsvContext {
  pipelines: readonly PipelinePayload[];
  /** By lower-case id (`RecordLookups.users`). */
  users: ReadonlyMap<string, UserPayload>;
  territories: ReadonlyMap<string, TerritoryPayload>;
  visitResults: ReadonlyMap<string, VisitResultPayload>;
  /** Visit count, last visit and its result are derived from activities, never on the wire. */
  stats?: DerivedStats;
}

// SwiftDtoa switches to exponent form above 2^54, far past any store value.
const SWIFT_EXPONENT_ABOVE = 2 ** 54;

/**
 * Swift `String(Double)` (SwiftDtoa), as the iPhone writes Value, Latitude and Longitude: the shortest digits that
 * read back to the same double, whole numbers with ".0" ("12500.0"), and exponent form with a two-digit exponent
 * below 1e-4 ("1e-05": a longitude a few metres from Greenwich) and for huge values. `toExponential()` without an
 * argument also picks the shortest round-trip digits, so only the layout differs from JavaScript's.
 */
export function swiftDouble(x: number): string {
  if (Number.isNaN(x)) return 'nan';
  if (!Number.isFinite(x)) return x > 0 ? 'inf' : '-inf';
  if (x === 0) return Object.is(x, -0) ? '-0.0' : '0.0';
  const abs = Math.abs(x);
  if (abs < 1e-4 || abs > SWIFT_EXPONENT_ABOVE) {
    return x.toExponential().replace(/e([+-])(\d)$/, (_, sign: string, digit: string) => `e${sign}0${digit}`);
  }
  const s = String(x); // plain decimal in this range (JavaScript only switches to exponents below 1e-6 or from 1e21)
  return Number.isInteger(x) ? `${s}.0` : s;
}

// A leading + or - is only dangerous when the rest is not a number (Swift's "-1e-05" included) or a phone number
// ("+1 (312) 555-0100" and "-87.62" stay as they are).
const PLAIN_NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i;
const PHONE_LIKE = /^[+-][\d\s().,/-]*$/;

/** True when a spreadsheet would treat the cell as a formula. */
export function isFormulaLike(s: string): boolean {
  if (s === '') return false;
  const c = s[0];
  if (c === '=' || c === '@' || c === '\t' || c === '\r') return true;
  if (c === '+' || c === '-') return !(PLAIN_NUMBER.test(s) || PHONE_LIKE.test(s));
  return false;
}

/** `CSVService.escape` plus the formula guard: quote when the cell holds a comma, quote or line break. */
export function csvField(value: string): string {
  const s = isFormulaLike(value) ? `'${value}` : value;
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** ISO 8601 without fractional seconds, as `ISO8601DateFormatter()` writes it. */
function isoDate(d: Date | undefined): string {
  return d && Number.isFinite(d.getTime()) ? toWireDate(d) : '';
}

/** One store's cells, in `CSV_HEADER` order (unescaped). */
export function recordCsvCells(r: RecordPayload, ctx: CsvContext): string[] {
  const s = statsOf(ctx.stats, r.id);
  const created = parseWireDate(r.createdAt);
  return [
    RECORD_TYPE_TITLES[r.type] ?? r.type,
    r.name,
    r.company,
    r.jobTitle,
    r.address.street,
    r.address.city,
    r.address.state,
    r.address.postalCode,
    r.coordinate ? swiftDouble(r.coordinate.latitude) : '',
    r.coordinate ? swiftDouble(r.coordinate.longitude) : '',
    r.phone,
    r.email,
    r.website,
    stageFor(r, ctx.pipelines)?.name ?? '',
    r.priority,
    swiftDouble(r.value),
    userName(ctx.users, r.ownerId),
    lookup(ctx.territories, r.territoryId)?.name ?? '',
    isoDate(s.lastVisitAt),
    lookup(ctx.visitResults, s.lastVisitResultId)?.name ?? '',
    String(s.visitCount),
    r.tags.join('; '),
    r.notes,
    created ? isoDate(created) : r.createdAt,
  ];
}

/** The whole file: header, then one line per store, joined with "\n" as on iOS. */
export function recordsCsv(records: readonly RecordPayload[], ctx: CsvContext): string {
  const lines = [CSV_HEADER.join(',')];
  for (const r of records) lines.push(recordCsvCells(r, ctx).map(csvField).join(','));
  return lines.join('\n');
}

/** "pipeline-residential_2026-10-01_1432.csv" (iOS `CSVFile.stampedName`, local time, plus the pipeline's name). */
export function csvFileName(pipelineName: string, now: Date = new Date()): string {
  const slug =
    pipelineName
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '') // accents, split off by NFKD
      .replace(/[^A-Za-z0-9]+/g, '-')
      .toLowerCase()
      .slice(0, 48)
      .replace(/^-+|-+$/g, '') || 'board';
  const p2 = (n: number) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}-${p2(now.getMonth() + 1)}-${p2(now.getDate())}_${p2(now.getHours())}${p2(now.getMinutes())}`;
  return `pipeline-${slug}_${stamp}.csv`;
}
